import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import VimeoPlayer from "@vimeo/player";
import * as iframeApiLoader from "@kinescope/player-iframe-api-loader";

import LottieLayout from "./Layouts/Lottie";
import ImageLayout from "./Layouts/Image";
import VimeoLayout from "./Layouts/Vimeo";
import KinescopeLayout from "./Layouts/Kinescope";
import ProjectTypes from "./ProjectTypes";
import viewportArrowLeftIcon from "/assets/viewport-arrow-left.svg";
import viewportArrowRightIcon from "/assets/viewport-arrow-right.svg";

export default function ProjectCard({
  project,
}) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const playerRefs = useRef({});
  const currentLayout = project.layouts[currentIndex];

  const previousIndex = () => {
    setCurrentIndex(currentIndex => (currentIndex <= 0) ? project.layouts.length - 1 : currentIndex - 1);
  };

  const nextIndex = () => {
    setCurrentIndex(currentIndex => (currentIndex >= project.layouts.length - 1) ? 0 : currentIndex + 1);
  };

  useEffect(() => setCurrentIndex(0), [project.layouts]);
  useEffect(() => { syncPlayerStates(); }, [currentIndex, project.layouts]);

  const syncPlayerStates = async () => {
    if (!currentLayout || currentLayout.layout_type_codename !== "video") return;

    const currentPlayer = await findOrCreatePlayer(currentLayout);
    if (typeof currentPlayer.ready === "function") {
      currentPlayer.ready().then(() => {
        if (currentLayout.play_mode === "loop")
          currentPlayer.play();
      });
    } else {
      if (currentLayout.play_mode === "loop")
        await currentPlayer.play();
    }

    pauseAllExcept(currentLayout.id);
  };

  const findOrCreatePlayer = async (layout) => {
    if (playerRefs.current[layout.id]) return playerRefs.current[layout.id];

    if (/https?:\/\/vimeo\.com/.test(layout.video_url)) {
      playerRefs.current[layout.id] = new VimeoPlayer(`vimeo-${layout.id}`);
    } else if (/https?:\/\/kinescope\.io/.test(layout.video_url)) {
      const isLoop = layout.play_mode === "loop";
      const factory = await iframeApiLoader.load();
      playerRefs.current[layout.id] = await factory.create(
        `kinescope-${layout.id}`,
        {
          url: layout.video_url,
          behavior: {
            loop: isLoop,
            muted: isLoop,
          },
          ui: {
            controls: !isLoop,
          }
        }
      );
    }

    return playerRefs.current[layout.id];
  };

  const pauseAllExcept = (layoutId) => {
    project.layouts.forEach((layout) => {
      if (layout.id !== layoutId && playerRefs.current[layout.id]) {
        pausePlayer(layout.id);
      }
    });
  };

  const pausePlayer = async (layoutId) => {
    await playerRefs.current[layoutId].pause();
  };

  return currentLayout && (
    <div className="project-gallery">
      <div className="project-gallery__viewport-and-info">
        <div className="project-gallery__viewport">
          {project.layouts.length > 1 && (
            <>
              <div className="project-gallery__viewport__left" onClick={previousIndex}><img src={viewportArrowLeftIcon} /></div>
              <div className="project-gallery__viewport__right" onClick={nextIndex}><img src={viewportArrowRightIcon} /></div>
            </>
          )}

          {project.layouts.map((layout, index) => {
            return (
              <div
                key={layout.id}
                className={classNames("project-gallery__layout", { "project-gallery__layout--show": currentIndex === index })}
              >
                {layout.layout_type_codename === "image" && (
                  <ImageLayout key={layout.id} layout={layout} />
                )}

                {layout.layout_type_codename === "video" && /https?:\/\/vimeo\.com/.test(layout.video_url) && (
                  <VimeoLayout key={layout.id} layout={layout} />
                )}

                {layout.layout_type_codename === "video" && /https?:\/\/kinescope\.io/.test(layout.video_url) && (
                  <KinescopeLayout key={layout.id} layout={layout} />
                )}

                {layout.layout_type_codename === "lottie" && (
                  <LottieLayout key={layout.id} layout={layout} />
                )}
              </div>
            );
          })}

          <div className="project-gallery__indicators">
            {project.layouts.map((layout, index) => (
              <div
                className={`project-gallery__indicator ${currentIndex === index ? "project-gallery__indicator--current" : ""}`}
                key={layout.id}
              ></div>
            ))}
          </div>
        </div>

        <div className="project-gallery__info">
          <div className="project-gallery__info__top">
            <h2><a href={project.url}>{project.title}</a></h2>

            <ProjectTypes project_types={project.project_types} />
          </div>

          <div className="project-gallery__info__text">
            <p>{currentLayout.description}</p>
            <div><a href={project.url} className="btn">{t('common.more')}</a></div>
          </div>
        </div>
      </div>

      <div className="project-gallery__thumbnails">
        {project.layouts.map((layout, index) => {
          const isSelected = index === currentIndex;

          return (
            <div key={layout.id} className={classNames("project-gallery__thumbnail", { "project-gallery__thumbnail--selected": isSelected })} onClick={() => setCurrentIndex(index)}>
              {layout.layout_type_codename === "image" && (
                <img src={layout.image.url} />
              )}
              {layout.layout_type_codename === "video" && (
                <img src={layout.thumbnail_url} />
              )}
              {layout.layout_type_codename === "lottie" && (
                <div className="project-gallery__thumbnail__lottie"></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
